import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Measures the horizontal distance from one tile-centre to the next
 * (`width + gap`) at the breakpoint Tailwind currently resolves to.
 * Reads it straight off the live DOM so the slot animation tracks
 * container-query breakpoints (260 → 280 → 360 → 420 px) without
 * having to duplicate that table in JS. Re-measures on resize.
 *
 * Attach the returned `ref` to one slot tile (any will do; we use
 * the lane's initial centre) and read `tileStep` from there.
 */
export function useTileStep() {
  const ref = useRef<HTMLDivElement>(null);
  const [tileStep, setTileStep] = useState(0);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      const sibling = el.nextElementSibling as HTMLElement | null;
      if (sibling) {
        const sibRect = sibling.getBoundingClientRect();
        setTileStep(sibRect.left - rect.left);
      } else {
        setTileStep(rect.width);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return { ref, tileStep };
}
